import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button, Input, Label } from '@clarix/ui';
import {
  useRequestPasswordReset,
  useVerifyResetOtp,
  useResetPassword,
  getApiErrorMessage,
} from '@/core/api';
import { PasswordInput } from './PasswordInput';
import { OtpInput } from './OtpInput';
import { PasswordErrorMessage } from './PasswordErrorMessage';

/**
 * /forgot-password — logged-out password recovery.
 *
 * Four steps in one page, matching the three public endpoints:
 *   email    → POST /auth/forgot-password    (a code is mailed)
 *   otp      → POST /auth/verify-reset-otp   (code → single-use reset token)
 *   password → POST /auth/reset-password     (token + new password)
 *   success  → back to /login
 *
 * The reset token lives in component state only — never in localStorage. It is
 * single-use and expires in 10 minutes server-side, so a reload legitimately
 * sends the user back to the start.
 *
 * No session is minted here by design: the user signs in normally afterwards,
 * so 2FA and the password-policy gate still run.
 */

/** Matches the login page's first-resend cooldown. */
const RESEND_COOLDOWN_SECONDS = 60;

type Step = 'email' | 'otp' | 'password' | 'success';

export function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');

  return (
    <div className="mx-auto w-full max-w-sm">
      {step === 'email' && (
        <EmailStep
          onSent={(sentTo) => {
            setEmail(sentTo);
            setStep('otp');
          }}
        />
      )}
      {step === 'otp' && (
        <OtpStep
          email={email}
          onBack={() => setStep('email')}
          onVerified={(token) => {
            setResetToken(token);
            setStep('password');
          }}
        />
      )}
      {step === 'password' && (
        <PasswordStep
          resetToken={resetToken}
          onExpired={() => setStep('email')}
          onDone={() => {
            // Reset complete (server flag cleared) — drop the force-reset sticky
            // flag so the user can proceed to /login and sign in normally.
            sessionStorage.removeItem('clarix.pwreset.required');
            setStep('success');
          }}
        />
      )}
      {step === 'success' && <SuccessStep />}
    </div>
  );
}

// ─── Step 1: email ──────────────────────────────────────────────────────────

function EmailStep({ onSent }: { onSent: (email: string) => void }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const request = useRequestPasswordReset();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await request.mutateAsync({ email });
      onSent(email);
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <Heading
        title="Forgot your password?"
        subtitle="Enter your email and we'll send you a 6-digit code to reset it."
      />

      <div className="space-y-1.5">
        <Label htmlFor="fp-email">Email</Label>
        <Input
          id="fp-email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
      </div>

      {error && <ErrorText>{error}</ErrorText>}

      <Button type="submit" className="mt-5 w-full" disabled={request.isPending || !email}>
        {request.isPending ? <Spinner /> : 'Send reset code'}
      </Button>

      <BackToSignIn />
    </form>
  );
}

// ─── Step 2: OTP ────────────────────────────────────────────────────────────

function OtpStep({
  email,
  onBack,
  onVerified,
}: {
  email: string;
  onBack: () => void;
  onVerified: (resetToken: string) => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const verify = useVerifyResetOtp();
  const resend = useRequestPasswordReset();

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      onVerified(await verify.mutateAsync({ email, code }));
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  };

  const doResend = async () => {
    setError(null);
    try {
      await resend.mutateAsync({ email });
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <Heading
        title="Check your email"
        subtitle={`We sent a 6-digit code to ${email}. It expires in 10 minutes.`}
      />

      <OtpInput value={code} onChange={setCode} ariaLabel="Password reset code" />

      {error && <ErrorText>{error}</ErrorText>}

      <Button
        type="submit"
        className="mt-5 w-full"
        disabled={verify.isPending || code.length !== 6}
      >
        {verify.isPending ? <Spinner /> : 'Verify code'}
      </Button>

      <div className="mt-4 flex items-center justify-between text-xl">
        <button
          type="button"
          onClick={onBack}
          className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          Use a different email
        </button>
        <button
          type="button"
          onClick={doResend}
          disabled={cooldown > 0 || resend.isPending}
          className="font-medium text-indigo-600 hover:text-indigo-700 disabled:text-slate-400 dark:text-indigo-400"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
      </div>
    </form>
  );
}

// ─── Step 3: new password ───────────────────────────────────────────────────

function PasswordStep({
  resetToken,
  onExpired,
  onDone,
}: {
  resetToken: string;
  onExpired: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const reset = useResetPassword();

  const mismatch = confirm.length > 0 && password !== confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    try {
      await reset.mutateAsync({ reset_token: resetToken, new_password: password });
      onDone();
    } catch (err) {
      setError(err);
      // A spent/expired token can only be recovered by requesting a new code.
      if (/expired|no longer valid/i.test(getApiErrorMessage(err))) setTimeout(onExpired, 2000);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <Heading title="Choose a new password" subtitle="Pick something you haven't used before." />

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="fp-new">New password</Label>
          <PasswordInput
            id="fp-new"
            autoComplete="new-password"
            placeholder="Enter a new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="fp-confirm">Confirm password</Label>
          <PasswordInput
            id="fp-confirm"
            autoComplete="new-password"
            placeholder="Re-enter the new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
      </div>

      {mismatch && <ErrorText>Passwords do not match.</ErrorText>}
      {error != null && !mismatch && (
        <ErrorText>
          <PasswordErrorMessage error={error} />
        </ErrorText>
      )}

      <Button
        type="submit"
        className="mt-5 w-full"
        disabled={reset.isPending || !password || !confirm || mismatch}
      >
        {reset.isPending ? <Spinner /> : 'Reset password'}
      </Button>
    </form>
  );
}

// ─── Step 4: success ────────────────────────────────────────────────────────

function SuccessStep() {
  const navigate = useNavigate();
  return (
    <div className="text-center">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" strokeWidth={1.5} />
      <h1 className="mt-4 text-4xl font-semibold text-slate-900 dark:text-slate-100">
        Password reset
      </h1>
      <p className="mt-1 text-xl text-slate-500">
        Sign in with your new password to continue.
      </p>
      <Button className="mt-5 w-full" onClick={() => navigate('/login')}>
        Back to sign in
      </Button>
    </div>
  );
}

// ─── Small shared bits ──────────────────────────────────────────────────────

function Heading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-5">
      <h1 className="text-4xl font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
      <p className="mt-1 text-xl text-slate-500">{subtitle}</p>
    </div>
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 text-xl text-red-600 dark:text-red-400" role="alert">
      {children}
    </div>
  );
}

function Spinner() {
  return <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />;
}

function BackToSignIn() {
  return (
    <Link
      to="/login"
      className="mt-4 flex items-center justify-center gap-1.5 text-xl text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
    >
      <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
      Back to sign in
    </Link>
  );
}
